import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Alert,
  LinearProgress,
  CircularProgress,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  ExpandMore,
  Receipt,
  AccessTime,
  People,
  ShoppingCart,
  DragIndicator,
} from "@mui/icons-material";
import api from "../../services/api";

// 条件タイプ
const CONDITION_TYPES = [
  { id: "member", name: "会員ランク", icon: <People fontSize="small" /> },
  { id: "time_range", name: "時間帯", icon: <AccessTime fontSize="small" /> },
  { id: "order_type", name: "注文タイプ", icon: <ShoppingCart fontSize="small" /> },
];

// 会員ランク
const MEMBER_RANKS = [
  { id: "regular", name: "レギュラー" },
  { id: "silver", name: "シルバー" },
  { id: "gold", name: "ゴールド" },
  { id: "platinum", name: "プラチナ" },
  { id: "vip", name: "VIP" },
];

// 注文タイプ
const ORDER_TYPES = [
  { id: "eat_in", name: "イートイン" },
  { id: "takeout", name: "テイクアウト" },
  { id: "delivery", name: "デリバリー" },
];

const ReceiptRulesSettings = () => {
  const { storeId } = useParams();
  const [rules, setRules] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);

  const [formData, setFormData] = useState({
    ruleName: "",
    priority: 100,
    conditionType: "member",
    memberRank: "",
    startHour: 0,
    endHour: 24,
    orderType: "",
    customHeader: "",
    customFooter: "",
    additionalMessage: "",
    isActive: true,
  });

  const fetchRules = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/stores/${storeId}/receipt-rules`);
      // 優先度でソート
      const sortedRules = (response.data || []).sort(
        (a, b) => a.priority - b.priority
      );
      setRules(sortedRules);
    } catch (err) {
      console.error("レシートルールの取得に失敗しました:", err);
      if (err.response?.status !== 404) {
        setError("レシートルールの取得に失敗しました");
      }
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const handleOpenDialog = (rule = null) => {
    if (rule) {
      setEditingRule(rule);
      const conditions = rule.conditions || {};
      setFormData({
        ruleName: rule.ruleName || "",
        priority: rule.priority || 100,
        conditionType: conditions.condition_type || "member",
        memberRank: conditions.member_rank || "",
        startHour: conditions.start_hour || 0,
        endHour: conditions.end_hour || 24,
        orderType: conditions.order_type || "",
        customHeader: rule.customHeader || "",
        customFooter: rule.customFooter || "",
        additionalMessage: rule.additionalMessage || "",
        isActive: rule.isActive !== false,
      });
    } else {
      setEditingRule(null);
      setFormData({
        ruleName: "",
        priority: 100,
        conditionType: "member",
        memberRank: "",
        startHour: 0,
        endHour: 24,
        orderType: "",
        customHeader: "",
        customFooter: "",
        additionalMessage: "",
        isActive: true,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingRule(null);
  };

  const handleChange = (field) => (e) => {
    const value =
      e.target.type === "checkbox"
        ? e.target.checked
        : e.target.type === "number"
          ? parseInt(e.target.value, 10)
          : e.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const buildConditions = () => {
    const base = { condition_type: formData.conditionType };

    switch (formData.conditionType) {
      case "member":
        return { ...base, member_rank: formData.memberRank };
      case "time_range":
        return {
          ...base,
          start_hour: formData.startHour,
          end_hour: formData.endHour,
        };
      case "order_type":
        return { ...base, order_type: formData.orderType };
      default:
        return base;
    }
  };

  const handleSubmit = async () => {
    if (!formData.ruleName) {
      setError("ルール名は必須です");
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const payload = {
        ruleName: formData.ruleName,
        priority: formData.priority,
        conditions: buildConditions(),
        customHeader: formData.customHeader || null,
        customFooter: formData.customFooter || null,
        additionalMessage: formData.additionalMessage || null,
        isActive: formData.isActive,
      };

      if (editingRule) {
        await api.put(
          `/api/stores/${storeId}/receipt-rules/${editingRule.id}`,
          payload
        );
      } else {
        await api.post(`/api/stores/${storeId}/receipt-rules`, payload);
      }

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      handleCloseDialog();
      fetchRules();
    } catch (err) {
      console.error("レシートルールの保存に失敗しました:", err);
      setError(
        err.response?.data?.message || "レシートルールの保存に失敗しました"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ruleId) => {
    if (!window.confirm("このルールを削除しますか？")) return;

    try {
      await api.delete(`/api/stores/${storeId}/receipt-rules/${ruleId}`);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      fetchRules();
    } catch (err) {
      console.error("レシートルールの削除に失敗しました:", err);
      setError("レシートルールの削除に失敗しました");
    }
  };

  const handleToggleActive = async (rule) => {
    try {
      await api.put(`/api/stores/${storeId}/receipt-rules/${rule.id}`, {
        ...rule,
        isActive: !rule.isActive,
      });
      fetchRules();
    } catch (err) {
      console.error("ルール状態の更新に失敗しました:", err);
      setError("ルール状態の更新に失敗しました");
    }
  };

  const getConditionDescription = (rule) => {
    const conditions = rule.conditions || {};
    switch (conditions.condition_type) {
      case "member":
        const rank = MEMBER_RANKS.find((r) => r.id === conditions.member_rank);
        return `会員ランク: ${rank?.name || conditions.member_rank}`;
      case "time_range":
        return `時間帯: ${conditions.start_hour}:00 - ${conditions.end_hour}:00`;
      case "order_type":
        const type = ORDER_TYPES.find((t) => t.id === conditions.order_type);
        return `注文タイプ: ${type?.name || conditions.order_type}`;
      default:
        return "条件なし";
    }
  };

  const getConditionIcon = (type) => {
    const condType = CONDITION_TYPES.find((c) => c.id === type);
    return condType?.icon || <Receipt fontSize="small" />;
  };

  return (
    <Box aria-busy={loading || saving}>
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      <Typography variant="h4" gutterBottom>
        条件別レシート設定
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        条件に応じてレシートのヘッダー・フッター・メッセージをカスタマイズします。
        優先度の数値が小さいルールが先に評価されます。
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          処理が完了しました
        </Alert>
      )}

      {/* ルール一覧 */}
      <Card>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Receipt color="primary" />
              <Typography variant="h6">レシートルール一覧</Typography>
              <Chip
                label={`${rules.filter((r) => r.isActive).length}/${rules.length} 有効`}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
            >
              ルール追加
            </Button>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width={50}>優先度</TableCell>
                  <TableCell>ルール名</TableCell>
                  <TableCell>条件</TableCell>
                  <TableCell>カスタマイズ内容</TableCell>
                  <TableCell align="center">有効</TableCell>
                  <TableCell align="center">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rules.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      レシートルールが登録されていません
                    </TableCell>
                  </TableRow>
                ) : (
                  rules.map((rule) => (
                    <TableRow
                      key={rule.id}
                      sx={{
                        opacity: rule.isActive ? 1 : 0.5,
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <DragIndicator
                            fontSize="small"
                            color="disabled"
                          />
                          <Chip label={rule.priority} size="small" />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {rule.ruleName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {getConditionIcon(rule.conditions?.condition_type)}
                          <Typography variant="body2">
                            {getConditionDescription(rule)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                          {rule.customHeader && (
                            <Chip label="ヘッダー" size="small" variant="outlined" />
                          )}
                          {rule.customFooter && (
                            <Chip label="フッター" size="small" variant="outlined" />
                          )}
                          {rule.additionalMessage && (
                            <Chip label="メッセージ" size="small" variant="outlined" />
                          )}
                          {!rule.customHeader &&
                            !rule.customFooter &&
                            !rule.additionalMessage && (
                              <Typography variant="caption" color="text.secondary">
                                なし
                              </Typography>
                            )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Switch
                          checked={rule.isActive}
                          onChange={() => handleToggleActive(rule)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(rule)}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(rule.id)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* 説明 */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>ルール評価の仕組み</strong>
          <br />
          レシート印刷時、優先度が小さい順にルールが評価されます。
          最初にマッチしたルールのカスタマイズ内容が適用されます。
        </Typography>
      </Alert>

      {/* ルール編集ダイアログ */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingRule ? "レシートルールを編集" : "新規レシートルール"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* 基本設定 */}
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="ルール名"
                value={formData.ruleName}
                onChange={handleChange("ruleName")}
                required
                placeholder="例: ゴールド会員向けメッセージ"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="優先度（小さいほど優先）"
                type="number"
                value={formData.priority}
                onChange={handleChange("priority")}
                inputProps={{ min: 1, max: 9999 }}
              />
            </Grid>

            {/* 条件設定 */}
            <Grid item xs={12}>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle1">条件設定</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth>
                        <InputLabel>条件タイプ</InputLabel>
                        <Select
                          value={formData.conditionType}
                          onChange={handleChange("conditionType")}
                          label="条件タイプ"
                        >
                          {CONDITION_TYPES.map((type) => (
                            <MenuItem key={type.id} value={type.id}>
                              <Box
                                sx={{ display: "flex", alignItems: "center", gap: 1 }}
                              >
                                {type.icon}
                                {type.name}
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    {formData.conditionType === "member" && (
                      <Grid item xs={12} sm={8}>
                        <FormControl fullWidth>
                          <InputLabel>会員ランク</InputLabel>
                          <Select
                            value={formData.memberRank}
                            onChange={handleChange("memberRank")}
                            label="会員ランク"
                          >
                            {MEMBER_RANKS.map((rank) => (
                              <MenuItem key={rank.id} value={rank.id}>
                                {rank.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    )}

                    {formData.conditionType === "time_range" && (
                      <>
                        <Grid item xs={12} sm={4}>
                          <FormControl fullWidth>
                            <InputLabel>開始時刻</InputLabel>
                            <Select
                              value={formData.startHour}
                              onChange={handleChange("startHour")}
                              label="開始時刻"
                            >
                              {[...Array(24)].map((_, i) => (
                                <MenuItem key={i} value={i}>
                                  {i}:00
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <FormControl fullWidth>
                            <InputLabel>終了時刻</InputLabel>
                            <Select
                              value={formData.endHour}
                              onChange={handleChange("endHour")}
                              label="終了時刻"
                            >
                              {[...Array(25)].map((_, i) => (
                                <MenuItem key={i} value={i}>
                                  {i}:00
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      </>
                    )}

                    {formData.conditionType === "order_type" && (
                      <Grid item xs={12} sm={8}>
                        <FormControl fullWidth>
                          <InputLabel>注文タイプ</InputLabel>
                          <Select
                            value={formData.orderType}
                            onChange={handleChange("orderType")}
                            label="注文タイプ"
                          >
                            {ORDER_TYPES.map((type) => (
                              <MenuItem key={type.id} value={type.id}>
                                {type.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    )}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>

            {/* カスタマイズ内容 */}
            <Grid item xs={12}>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle1">カスタマイズ内容</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="カスタムヘッダー"
                        value={formData.customHeader}
                        onChange={handleChange("customHeader")}
                        multiline
                        rows={2}
                        placeholder="レシート上部に表示するメッセージ"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="追加メッセージ"
                        value={formData.additionalMessage}
                        onChange={handleChange("additionalMessage")}
                        multiline
                        rows={2}
                        placeholder="明細の後に表示するメッセージ"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="カスタムフッター"
                        value={formData.customFooter}
                        onChange={handleChange("customFooter")}
                        multiline
                        rows={2}
                        placeholder="レシート下部に表示するメッセージ"
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>

            {/* 有効/無効 */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={handleChange("isActive")}
                  />
                }
                label="このルールを有効にする"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>キャンセル</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={saving || !formData.ruleName}
          >
            {saving ? <CircularProgress size={20} /> : "保存"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReceiptRulesSettings;
